import React, { useState, useEffect } from "react";
import Footer from "../components/Navbars/footer";
import {
  Plus,
  Edit2,
  CheckCircle,
  XCircle,
  Loader2,
  Link as LinkIcon,
  ExternalLink,
  ShieldCheck,
} from "lucide-react";
import {
  useComplianceModules,
  useCreateComplianceModule,
  useUpdateComplianceModule,
  useToggleComplianceModule,
} from "../adapters/Requests";

const ComplianceSettings = () => {
  const { data: modules = [], isLoading, isFetching } = useComplianceModules();
  const createModule = useCreateComplianceModule();
  const updateModule = useUpdateComplianceModule();
  const toggleModule = useToggleComplianceModule();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [showWarning, setShowWarning] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    link: "",
  });

  useEffect(() => {
    const handleResize = () => setShowWarning(window.innerWidth < 800);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingModule) {
        await updateModule.mutateAsync({ id: editingModule.id, ...formData });
      } else {
        await createModule.mutateAsync(formData);
      }
      setIsModalOpen(false);
      setEditingModule(null);
      setFormData({ title: "", description: "", link: "" });
    } catch (error) {
      console.error("Error saving module", error);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await toggleModule.mutateAsync(id);
    } catch (error) {
      console.error("Error toggling status", error);
    }
  };

  const openEdit = (module) => {
    setEditingModule(module);
    setFormData({
      title: module.title,
      description: module.description || "",
      link: module.link || "",
    });
    setIsModalOpen(true);
  };

  const isSubmitting = createModule.isPending || updateModule.isPending;

  return (
    <>
      <div className="w-full max-w-[95%] 2xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-50 rounded-lg">
                <ShieldCheck className="w-8 h-8 text-green-700" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                Compliance and Learning Modules
              </h1>
            </div>
            <p className="text-gray-500 text-md max-w-2xl">
              Oversee and manage mandatory learning modules to ensure intern
              compliance and safety standards.
            </p>
          </div>
          <button
            onClick={() => {
              setEditingModule(null);
              setFormData({ title: "", description: "", link: "" });
              setIsModalOpen(true);
            }}
            className="flex items-center justify-center gap-2 bg-green-700 text-white px-6 py-3 rounded-lg hover:bg-green-800 transition-all font-semibold shadow-md hover:shadow-lg transform active:scale-95"
          >
            <Plus size={20} />
            Add New Module
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-green-600 animate-spin" />
            </div>
          )}
          <div className="p-0">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-32 text-green-700">
                <Loader2 className="animate-spin w-12 h-12 mb-4" />
                <p className="text-base font-medium text-gray-500">
                  Retrieving compliance data...
                </p>
              </div>
            ) : modules.length === 0 ? (
              <div className="text-center py-32 bg-gray-50/30">
                <div className="bg-white border border-gray-100 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                  <LinkIcon className="text-green-600 w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  No Modules Active
                </h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  There are currently no compliance modules available. Add a new
                  module to get started.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="py-5 px-8 text-xs font-bold text-gray-500 uppercase tracking-wider w-32">
                        Status
                      </th>
                      <th className="py-5 px-8 text-xs font-bold text-gray-500 uppercase tracking-wider">
                        Module Information
                      </th>
                      <th className="py-5 px-8 text-xs font-bold text-gray-500 uppercase tracking-wider w-64">
                        Resource Link
                      </th>
                      <th className="py-5 px-8 text-xs font-bold text-gray-500 uppercase tracking-wider w-40 text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {modules.map((mod) => (
                      <tr
                        key={mod.id}
                        className="hover:bg-green-50/30 transition-colors group"
                      >
                        <td className="py-5 px-8 align-top">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${
                              mod.is_active
                                ? "bg-green-100 text-green-800 border-green-200"
                                : "bg-gray-100 text-gray-600 border-gray-200"
                            }`}
                          >
                            {mod.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="py-5 px-8 align-top">
                          <h3 className="text-base font-bold text-gray-900 mb-1">
                            {mod.title}
                          </h3>
                          <p className="text-sm text-gray-500 leading-relaxed max-w-3xl">
                            {mod.description}
                          </p>
                        </td>
                        <td className="py-5 px-8 align-top">
                          <a
                            href={mod.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm text-green-700 hover:text-green-900 hover:underline font-medium p-1 rounded-md hover:bg-green-50 transition-colors"
                          >
                            <ExternalLink size={14} /> Open Resource
                          </a>
                        </td>
                        <td className="py-5 px-8 align-top text-right">
                          <div className="flex items-center justify-end gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => openEdit(mod)}
                              className="p-2 text-gray-400 hover:text-green-700 hover:bg-green-50 rounded-lg transition-all"
                              title="Edit Module"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              onClick={() => handleToggleStatus(mod.id)}
                              disabled={toggleModule.isPending}
                              className={`p-2 rounded-lg transition-all ${
                                mod.is_active
                                  ? "text-gray-400 hover:text-red-600 hover:bg-red-50"
                                  : "text-gray-400 hover:text-green-600 hover:bg-green-50"
                              }`}
                              title={mod.is_active ? "Deactivate" : "Activate"}
                            >
                              {mod.is_active ? (
                                <XCircle size={18} />
                              ) : (
                                <CheckCircle size={18} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
            onClick={() => setIsModalOpen(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 transform transition-all">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 border-b border-gray-100 pb-4">
              {editingModule ? "Edit Module Details" : "Create New Module"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Module Title
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-green-100 focus:border-green-500 outline-none transition-all text-sm bg-gray-50 focus:bg-white"
                  placeholder="e.g. Corporate Ethics & Conduct"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-green-100 focus:border-green-500 outline-none transition-all text-sm resize-none h-32 bg-gray-50 focus:bg-white leading-relaxed"
                  placeholder="Provide a brief overview of what this module entails..."
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Resource URL
                </label>
                <div className="relative">
                  <LinkIcon
                    className="absolute left-4 top-3.5 text-gray-400"
                    size={18}
                  />
                  <input
                    type="url"
                    required
                    value={formData.link}
                    onChange={(e) =>
                      setFormData({ ...formData, link: e.target.value })
                    }
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-green-100 focus:border-green-500 outline-none transition-all text-sm bg-gray-50 focus:bg-white"
                    placeholder="https://knowledgecenter.nnpcgroup.com/..."
                  />
                </div>
              </div>
              <div className="flex gap-4 pt-6">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-6 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-semibold transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-green-700 text-white rounded-xl hover:bg-green-800 font-semibold shadow-lg shadow-green-200 transition-all flex items-center justify-center gap-2"
                >
                  {isSubmitting && <Loader2 className="animate-spin w-5 h-5" />}
                  {editingModule ? "Save Changes" : "Create Module"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ComplianceSettings;
